// A task on phones, as a full page over everything (instead of the desktop modal): Details with the
// fields as chips and the usual sections, and Activity with the comment box at the bottom.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowUp, Calendar, ChevronLeft, Ellipsis, Flag, Folder, Hourglass, ListTodo, Pause, Play, Tag, Timer, UserRound } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { useDb, useMe } from '@/lib/store';
import { useOpenTask } from '@/lib/nav';
import { effectiveStatuses, statusOf } from '@/lib/hierarchy';
import { runningTimer, startTimer, stopTimer, trackedMs, updateTask } from '@/lib/actions';
import { dueTone, formatDue, formatDuration } from '@/lib/format';
import { pushRecent } from '@/lib/ui';
import { Popover } from '@/components/ui/Popover';
import { AvatarStack } from '@/components/ui/Avatar';
import { StatusMenu, StatusPill } from '@/components/fields/Status';
import { PeopleMenu } from '@/components/fields/People';
import { PriorityFlag, PriorityMenu } from '@/components/fields/Priority';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { TagChip, TagMenu } from '@/components/fields/Tags';
import { TimeEstimateEditor } from '@/components/fields/TaskCells';
import { ShareButton } from '@/components/ShareButton';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { TaskDescription } from '@/task/TaskDescription';
import { SubtasksSection } from '@/task/SubtasksSection';
import { ChecklistsSection } from '@/task/ChecklistsSection';
import { TaskFields } from '@/task/TaskFields';
import { RelationshipsSection } from '@/task/RelationshipsSection';
import { AttachmentsSection } from '@/task/AttachmentsSection';
import { ActivityPanel } from '@/task/ActivityPanel';
import { openSheet } from './sheets';

function Title({ task }: { task: Task }) {
  const [draft, setDraft] = useState(task.name);
  useEffect(() => setDraft(task.name), [task.name]);
  const save = () => {
    const name = draft.trim();
    if (name && name !== task.name) updateTask(task.id, { name });
    else setDraft(task.name);
  };
  return (
    <textarea
      className="m-t-title"
      value={draft}
      rows={1}
      onChange={(e) => setDraft(e.target.value.replace(/\n/g, ' '))}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      aria-label="Task name"
    />
  );
}

function TimerButton({ task, chip }: { task: Task; chip?: boolean }) {
  const me = useMe();
  const running = useDb((db) => runningTimer(db, me));
  const total = useDb((db) => trackedMs(db, task.id));
  const on = running?.taskId === task.id;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
  const toggle = () => (on ? stopTimer() : startTimer(task.id));
  if (!chip)
    return (
      <button type="button" className={clsx('m-icon-btn', { on })} onClick={toggle} aria-label={on ? 'Stop timer' : 'Start timer'}>
        {on ? <Pause size={20} fill="currentColor" /> : <Timer size={21} />}
      </button>
    );
  return (
    <button type="button" className={clsx('m-chip', { set: on || total > 0, running: on })} onClick={toggle}>
      {on ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" />}
      {on ? formatDuration(Date.now() - running!.start, { seconds: true }) : total ? formatDuration(total) : 'Track time'}
    </button>
  );
}

function Chips({ task }: { task: Task }) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDb((db) => effectiveStatuses(db, task.listId));
  const closed = isCompleteType(status.type);
  const spaceId = useDb((db) => db.lists[task.listId]?.spaceId ?? '');
  const dates = task.startDate || task.dueDate ? [task.startDate && formatDue(task.startDate), task.dueDate && formatDue(task.dueDate)].filter(Boolean).join(' → ') : '';
  return (
    <div className="m-chips">
      <Popover
        content={(close) => (
          <StatusMenu
            statuses={statuses}
            currentId={task.statusId}
            onPick={(s) => {
              updateTask(task.id, { statusId: s.id });
              close();
            }}
          />
        )}
      >
        <button type="button" className="m-chip status">
          <StatusPill status={status} statuses={statuses} size="lg" />
        </button>
      </Popover>
      <Popover content={() => <PeopleMenu selected={task.assigneeIds} onChange={(ids) => updateTask(task.id, { assigneeIds: ids })} />}>
        <button type="button" className={clsx('m-chip', { set: task.assigneeIds.length > 0 })}>
          {task.assigneeIds.length ? <AvatarStack userIds={task.assigneeIds} size={20} max={4} /> : <UserRound size={16} />}
          {task.assigneeIds.length ? null : 'Assignees'}
        </button>
      </Popover>
      <Popover content={(close) => <DateRangeMenu start={task.startDate} due={task.dueDate} initialFocus="due" onChange={(startDate, dueDate) => updateTask(task.id, { startDate, dueDate })} onClose={close} />}>
        <button type="button" className={clsx('m-chip', { set: !!dates }, dates && `due-${dueTone(task.dueDate, closed)}`)}>
          <Calendar size={16} />
          {dates || 'Dates'}
        </button>
      </Popover>
      <Popover content={(close) => <PriorityMenu value={task.priority} onPick={(p) => (updateTask(task.id, { priority: p }), close())} />}>
        <button type="button" className={clsx('m-chip', { set: !!task.priority })}>
          {task.priority ? <PriorityFlag priority={task.priority} showLabel /> : <><Flag size={16} /> Priority</>}
        </button>
      </Popover>
      <TimerButton task={task} chip />
      <Popover content={(close) => <TimeEstimateEditor task={task} close={close} />}>
        <button type="button" className={clsx('m-chip', { set: !!task.timeEstimate })}>
          <Hourglass size={16} />
          {task.timeEstimate ? formatDuration(task.timeEstimate) : 'Time estimate'}
        </button>
      </Popover>
      <Popover content={() => <TagMenu spaceId={spaceId} selected={task.tagIds} onChange={(ids) => updateTask(task.id, { tagIds: ids })} />}>
        <button type="button" className={clsx('m-chip', { set: task.tagIds.length > 0 })}>
          {task.tagIds.length ? task.tagIds.map((id) => <TagChip key={id} tagId={id} />) : <><Tag size={16} /> Tags</>}
        </button>
      </Popover>
    </div>
  );
}

export function MobileTask({ taskId }: { taskId: string }) {
  const task = useDb((d) => d.tasks[taskId]);
  const parent = useDb((d) => (task?.parentId ? d.tasks[task.parentId] : undefined));
  const list = useDb((d) => (task ? d.lists[task.listId] : undefined));
  const folder = useDb((d) => (list?.folderId ? d.folders[list.folderId] : undefined));
  const [params, setParams] = useSearchParams();
  const openTask = useOpenTask();
  const [tab, setTab] = useState<'details' | 'activity'>('details');
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('t');
    setParams(next);
  };

  useEffect(() => {
    if (task) pushRecent({ kind: 'task', id: task.id });
  }, [task?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <div className="m-tpage" role="dialog" aria-modal="true" aria-label={task?.name ?? 'Task'}>
      <header className="m-header m-t-header">
        <button type="button" className="m-icon-btn" onClick={close} aria-label="Back">
          <ChevronLeft size={22} />
        </button>
        <span className="spacer" />
        {task && (
          <>
            <TimerButton task={task} />
            <ShareButton
              target={{ type: 'task', id: task.id }}
              link={() => {
                const url = new URL(window.location.href);
                url.searchParams.set('t', task.id);
                return url.toString();
              }}
              className="m-icon-btn m-share"
              iconSize={20}
            />
            <button type="button" className="m-icon-btn" onClick={() => openSheet('task-menu', task.id)} aria-label="Task settings">
              <Ellipsis size={22} />
            </button>
          </>
        )}
      </header>
      {!task ? (
        <div className="m-empty">
          <strong>This task isn’t available</strong>
          <span>It may have been deleted, or it isn’t shared with you.</span>
        </div>
      ) : (
        <>
          <div className="m-t-tabs" role="tablist">
            {(['details', 'activity'] as const).map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} className={clsx('m-t-tab', { on: tab === t })} onClick={() => setTab(t)}>
                {t === 'details' ? 'Details' : 'Activity'}
              </button>
            ))}
          </div>
          {tab === 'details' ? (
            <div className="m-scroll m-t-details">
              {parent && (
                <button type="button" className="m-t-parent" onClick={() => openTask(parent.id, { replace: true })}>
                  <ArrowUp size={16} /> Parent task: <span className="truncate">{parent.name}</span>
                </button>
              )}
              <div className="m-t-crumbs">
                {list && <SpaceAvatar spaceId={list.spaceId} size={20} />}
                {folder && (
                  <>
                    <span className="faint">/</span>
                    <Folder size={16} />
                    <span className="truncate">{folder.name}</span>
                  </>
                )}
                {list && (
                  <>
                    <span className="faint">/</span>
                    <ListTodo size={16} color={list.color ?? undefined} />
                    <span className="truncate">{list.name}</span>
                  </>
                )}
              </div>
              <Title task={task} />
              <Chips task={task} />
              <div className="m-t-desc">
                <TaskDescription task={task} />
              </div>
              <div className="m-t-sections">
                <SubtasksSection task={task} />
                <ChecklistsSection task={task} />
                <TaskFields task={task} />
                <RelationshipsSection task={task} />
                <AttachmentsSection task={task} />
              </div>
            </div>
          ) : (
            <div className="m-t-activity">
              <ActivityPanel task={task} />
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}
